import { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import api, { apiError, localISO } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2, Clock, Camera, MapPin, RefreshCw, ImageUp } from "lucide-react";

const READER_ID = "qwik-qr-reader";
const FILE_READER_ID = "qwik-file-reader";

// The workplace QR encodes a URL like https://app/scan?d=<token>; pull the token out (or accept a raw token).
function extractToken(text) {
  if (!text) return null;
  try {
    const u = new URL(text);
    return u.searchParams.get("d") || null;
  } catch {
    return text.trim() || null;
  }
}

function getLocation() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve({ lat: null, lng: null, denied: false });
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, denied: false }),
      (err) => resolve({ lat: null, lng: null, denied: err.code === 1 }),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  });
}

export default function Scan() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [state, setState] = useState("init"); // init | scanning | camera-error | working | success | error
  const [result, setResult] = useState(null);
  const [msg, setMsg] = useState("");
  const scannerRef = useRef(null);
  const fileInputRef = useRef(null);
  const submitting = useRef(false);

  const stopScanner = useCallback(async () => {
    const s = scannerRef.current;
    scannerRef.current = null;
    if (s) {
      try { await s.stop(); } catch {}
      try { await s.clear(); } catch {}
    }
  }, []);

  // Verify a token: request location (mandatory for on-site clock-in), then post to backend.
  const verify = useCallback(async (token) => {
    if (submitting.current) return;
    submitting.current = true;
    await stopScanner();
    setState("working");
    setMsg("Getting your location…");
    const loc = await getLocation();
    setMsg("Verifying workplace…");
    try {
      const { data } = await api.post("/attendance/scan", {
        token,
        timestamp: localISO(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        lat: loc.lat,
        lng: loc.lng,
      });
      setResult(data);
      setState("success");
    } catch (err) {
      setState("error");
      const detail = apiError(err.response?.data?.detail);
      setMsg(loc.denied && err.response?.status === 400
        ? "Location permission was blocked. Please allow location access for this site in your browser settings, then scan again."
        : detail);
    } finally {
      submitting.current = false;
    }
  }, [stopScanner]);

  const startScanner = useCallback(async () => {
    await stopScanner();
    submitting.current = false;
    setState("scanning");
    setMsg("");
    // html5-qrcode attaches to #READER_ID, which only mounts after the "scanning" state flushes.
    // Wait for the element to exist, otherwise the constructor throws and we wrongly show "camera unavailable".
    for (let i = 0; i < 25 && !document.getElementById(READER_ID); i++) {
      await new Promise((r) => setTimeout(r, 25));
    }
    // Live camera (getUserMedia) is blocked in embedded/iframe previews and on devices without a camera.
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setState("camera-error");
      setMsg("Your browser can't open a live camera here. Use “Scan from photo”, or scan the workplace QR with your phone's camera app.");
      return;
    }
    try {
      const scanner = new Html5Qrcode(READER_ID, { verbose: false });
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        (decodedText) => {
          const token = extractToken(decodedText);
          if (token) verify(token);
        },
        () => {} // ignore per-frame decode errors
      );
    } catch (e) {
      scannerRef.current = null;
      const s = String(e?.message || e || "").toLowerCase();
      setState("camera-error");
      if (s.includes("permission") || s.includes("notallowed") || s.includes("denied")) {
        setMsg("Camera permission was denied. Allow camera access and try again, or use “Scan from photo”.");
      } else if (s.includes("notfound") || s.includes("no camera") || s.includes("requested device")) {
        setMsg("No camera was found on this device. Use “Scan from photo”, or scan the workplace QR with your phone's camera app.");
      } else {
        setMsg("The live camera can't open here (this often happens inside embedded previews). Use “Scan from photo”, or scan the workplace QR with your phone's camera app.");
      }
    }
  }, [stopScanner, verify]);

  // Fallback: decode a QR from a photo the employee takes/uploads (works even when live camera is blocked).
  const scanFromFile = useCallback(async (file) => {
    if (!file) return;
    await stopScanner();
    setState("working");
    setMsg("Reading QR code…");
    try {
      const h = new Html5Qrcode(FILE_READER_ID, { verbose: false });
      const text = await h.scanFile(file, false);
      try { await h.clear(); } catch {}
      const token = extractToken(text);
      if (token) return verify(token);
      setState("error");
      setMsg("That image didn't contain a valid workplace QR code. Please try again.");
    } catch (e) {
      setState("error");
      setMsg("Couldn't read a QR code from that photo. Hold the code steady, in focus and well lit, then try again.");
    }
  }, [stopScanner, verify]);

  useEffect(() => {
    if (loading) return;
    const token = params.get("d");
    if (!user) {
      const back = encodeURIComponent(token ? `/scan?d=${token}` : "/scan");
      navigate(`/login?next=${back}`, { replace: true });
      return;
    }
    if (token) {
      verify(token); // opened via native camera on the workplace QR URL
    } else {
      startScanner(); // open in-app camera to scan the workplace QR
    }
    return () => { stopScanner(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading]);

  const clockedOut = result && result.clock_out;
  const pickPhoto = () => fileInputRef.current?.click();

  const PhotoButton = ({ primary }) => (
    <Button onClick={pickPhoto} variant={primary ? "default" : "outline"}
      className={primary ? "w-full bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" : "w-full border-emerald-500/40"}
      data-testid="scan-photo-btn">
      <ImageUp className="w-4 h-4 mr-2" /> Scan from photo
    </Button>
  );

  return (
    <div className="min-h-screen bg-[#0A0D0C] grid-bg flex items-center justify-center px-6 py-10">
      {/* hidden helpers, always mounted */}
      <input ref={fileInputRef} type="file" accept="image/*" capture="environment" className="hidden"
        onChange={(e) => scanFromFile(e.target.files?.[0])} data-testid="scan-file-input" />
      <div id={FILE_READER_ID} className="hidden" />

      <div className="card-emerald p-8 max-w-sm w-full text-center animate-fade-up" data-testid="scan-result">
        {state === "scanning" && (
          <>
            <div className="flex items-center justify-center gap-2 text-emerald-300 mb-4">
              <Camera className="w-5 h-5" />
              <h1 className="font-display text-lg font-bold">Scan workplace QR</h1>
            </div>
            <div id={READER_ID} data-testid="scan-camera" className="w-full overflow-hidden rounded-2xl border border-emerald-500/30 bg-black" style={{ minHeight: 260 }} />
            <p className="text-sm text-muted-foreground mt-4 flex items-center justify-center gap-1.5">
              <MapPin className="w-4 h-4" /> Point at the code shown at your workplace
            </p>
            <div className="mt-4 space-y-2">
              <PhotoButton />
              <Button variant="ghost" onClick={() => navigate("/app")} className="w-full" data-testid="scan-cancel-btn">Cancel</Button>
            </div>
          </>
        )}

        {state === "camera-error" && (
          <>
            <XCircle className="w-14 h-14 text-amber-400 mx-auto" />
            <h1 className="font-display text-lg font-bold mt-4">Camera unavailable</h1>
            <p className="text-muted-foreground mt-2 text-sm">{msg}</p>
            <div className="mt-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 p-3 text-xs text-emerald-200/90 text-left flex gap-2">
              <MapPin className="w-4 h-4 shrink-0 mt-0.5" />
              <span>Tip: open your phone's camera app and point it at the workplace QR — it opens QwikLogs and clocks you in automatically.</span>
            </div>
            <div className="mt-4 space-y-2">
              <PhotoButton primary />
              <Button onClick={startScanner} variant="outline" className="w-full border-emerald-500/40" data-testid="scan-retry-btn">
                <RefreshCw className="w-4 h-4 mr-2" /> Try camera again
              </Button>
              <Button variant="ghost" onClick={() => navigate("/app")} className="w-full">Back to app</Button>
            </div>
          </>
        )}

        {(state === "working" || state === "init") && (
          <>
            <Loader2 className="w-12 h-12 text-emerald-500 animate-spin mx-auto" />
            <h1 className="font-display text-xl font-bold mt-6">{msg || "Verifying…"}</h1>
          </>
        )}

        {state === "success" && (
          <>
            <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto" />
            <h1 className="font-display text-2xl font-bold mt-4">{clockedOut ? "Clocked out" : "Clocked in"}</h1>
            <p className="text-emerald-300 text-3xl font-display font-extrabold mt-2 flex items-center justify-center gap-2">
              <Clock className="w-6 h-6" />
              {new Date(clockedOut ? result.clock_out : result.clock_in).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </p>
            <p className="text-muted-foreground mt-1">{result.workplace_name || result.clock_out_workplace}</p>
            {result.verification_result === "requires_review" && <p className="text-amber-300 text-sm mt-3">Recorded — flagged for manager review.</p>}
            {result.early_out_request?.status === "pending" && <p className="text-amber-300 text-sm mt-3">Early clock-out submitted — you remain checked in until a manager approves.</p>}
            <Button onClick={() => navigate("/app")} className="mt-6 w-full bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" data-testid="scan-done-btn">Done</Button>
          </>
        )}

        {state === "error" && (
          <>
            <XCircle className="w-16 h-16 text-red-400 mx-auto" />
            <h1 className="font-display text-xl font-bold mt-4">Couldn't verify</h1>
            <p className="text-muted-foreground mt-2 text-sm">{msg}</p>
            <div className="mt-5 space-y-2">
              <Button onClick={startScanner} className="w-full bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" data-testid="scan-retry-btn">
                <RefreshCw className="w-4 h-4 mr-2" /> Scan again
              </Button>
              <PhotoButton />
              <Button variant="ghost" onClick={() => navigate("/app")} className="w-full">Back to app</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
